import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axiosClient from '../api/axiosClient';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token') || '');
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');

  const refreshUser = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axiosClient.get('/auth/me');
      if (res.data?.user) {
        setUser(res.data.user);
      }
    } catch (err) {
      console.error('[Auth Context] User refresh failed:', err);
    }
  }, [token]);

  // Fetch authenticated user profile on mount & poll every 5s for live wallet balance sync
  useEffect(() => {
    const fetchUser = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await axiosClient.get('/auth/me');
        setUser(res.data.user);
      } catch (err) {
        console.error('[Auth Context] Token validation failed:', err);
        logout();
      } finally {
        setLoading(false);
      }
    };

    fetchUser();

    const interval = setInterval(() => {
      if (token) {
        refreshUser();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [token, refreshUser]);

  const login = async (email, password) => {
    setAuthError('');
    try {
      const res = await axiosClient.post('/auth/login', { email, password });
      const { token: newToken, user: userData } = res.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      return { success: true, user: userData };
    } catch (err) {
      const msg = err.response?.data?.error || 'Login failed. Please check credentials.';
      setAuthError(msg);
      return { success: false, error: msg };
    }
  };

  const sendSignupOTP = async (email, referralCode) => {
    setAuthError('');
    try {
      const res = await axiosClient.post('/auth/send-signup-otp', { email, referralCode });
      return { success: true, message: res.data.message };
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to send signup verification OTP.';
      setAuthError(msg);
      return { success: false, error: msg };
    }
  };

  const register = async (name, email, password, referralCodeInput, otp) => {
    setAuthError('');
    try {
      const res = await axiosClient.post('/auth/register', {
        name,
        email,
        password,
        referralCodeInput,
        otp
      });
      const { token: newToken, user: userData } = res.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      return { success: true, user: userData };
    } catch (err) {
      const msg = err.response?.data?.error || 'Registration failed.';
      setAuthError(msg);
      return { success: false, error: msg };
    }
  };

  const sendForgotPasswordOTP = async (email) => {
    setAuthError('');
    try {
      const res = await axiosClient.post('/auth/forgot-password-otp', { email });
      return { success: true, message: res.data.message };
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to send password reset OTP.';
      setAuthError(msg);
      return { success: false, error: msg };
    }
  };

  const resetPassword = async (email, otp, newPassword) => {
    setAuthError('');
    try {
      const res = await axiosClient.post('/auth/reset-password', { email, otp, newPassword });
      return { success: true, message: res.data.message };
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to reset password.';
      setAuthError(msg);
      return { success: false, error: msg };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.clear();
    sessionStorage.clear();
    setToken('');
    setUser(null);
    setAuthError('');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        authError,
        setAuthError,
        refreshUser,
        login,
        sendSignupOTP,
        register,
        sendForgotPasswordOTP,
        resetPassword,
        logout,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin'
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
